import biLogger from '@wix/web-bi-logger';
import {
  AppLoadBiEvent,
  BiEvent,
  BiEventDefaultParams,
  BiEventIDs,
  BiEventParams,
  BiLogger,
  ChangePlanBiEvent,
  ChangeStepBiEvent,
  CheckoutPageBiEvent,
  CheckoutPageLoadedBiEvent,
  CheckoutPageViewTooltip,
  ClickOnFAQItem,
  ClickOnShowAllFeatures,
  ClickOnShowFeatures,
  ClickProductFeatureTooltipLinkBiEvent,
  DayfulBiEventIds,
  DoublePurchaseBiEvent,
  EventActionMap,
  PlanTypeInfoBiEvent,
  RequestCallCTABiEvent,
  RequestCallPopupLoadBiEvent,
  SelectCycleBiEvent,
  SelectPlanBiEvent,
  SelectPlanRadioButtonsMobileGrid,
  StickyHeaderMobileGrid,
  ViewCurrentPlanBiEvent,
  ViewCycleDiscountTooltip,
  ViewProductFeatureTooltipBiEvent,
  SubmitCheckoutReassignDomainFlow,
  AnonymousCtaBiEvent,
} from './BiLoggerTypes';
import { Brands } from 'enums';
import {
  LogObject,
  packagePickerCheckoutLoadFailedSrc17Evid728Params,
  packagePickerCycleSelection2ndStepClickContinueToCheckoutSrc17Evid3925Params,
  packagePickerCycleSelectionRevShareLoadedSrc17Evid3927Params,
} from '@wix/bi-logger-premium-data/v2/types';
import {
  packagePickerCheckoutLoadFailedSrc17Evid728,
  packagePickerCycleSelection2ndStepClickContinueToCheckoutSrc17Evid3925,
  packagePickerCycleSelectionRevShareLoadedSrc17Evid3927,
} from '@wix/bi-logger-premium-data/v2';

const brandHostMap: { [key: string]: string } = {
  [Brands.WIX]: 'www.wix.com',
  [Brands.EDITOR_X]: 'manage.editorx.com',
  [Brands.DAYFUL]: 'www.wix.com',
  studio: 'www.wix.com',
};

export const BiLoggerFactory = (
  defaults: BiEventDefaultParams,
  brand: Brands,
  _biLogger_ = biLogger,
): BiLogger => {
  const loggerDefaults = {
    referralAdditionalInfo: '',
    referer: '',
    ...defaults,
    abTesting: 'purchase_wix_plan_static',
    packagePickerType: defaults.packagePickerType,
  };

  const logger: {
    log(event: Partial<BiEvent>): void;
  } = _biLogger_
    .factory({
      host: `${brandHostMap[brand]}/store/bi`,
      endpoint: 'plansPurchase',
      useBeacon: true,
    })
    .updateDefaults(loggerDefaults)
    .logger();

  const loggerV2: {
    report<T>(event: LogObject<T>): void;
  } = _biLogger_.factory().updateDefaults(loggerDefaults).logger();

  function getBiEventData<T>(
    additionalParams: BiEventParams<T>,
    evid: BiEventIDs | DayfulBiEventIds,
  ): Partial<BiEvent> {
    return { evid, ...additionalParams, action: EventActionMap[evid || 0] };
  }

  const logBiEvent = (event: Partial<BiEvent>): void => {
    const logEvent = () => {
      Object.keys(event).forEach((key) => {
        const value = event[key];
        event[key] = typeof value === 'object' ? JSON.stringify(value) : value;
      });
      logger.log(event);
    };

    if (brand !== Brands.DAYFUL) {
      logEvent();
      return;
    }

    if (event.evid && DayfulBiEventIds[event.evid]) {
      logEvent();
    }
  };

  const logSelectPlanBiEvent = (
    additionalParams: BiEventParams<SelectPlanBiEvent>,
  ) => logBiEvent(getBiEventData(additionalParams, BiEventIDs.A_PLAN_SELECTED));

  const logChangeTabBiEvent = (
    additionalParams: BiEventParams<PlanTypeInfoBiEvent>,
  ) => logBiEvent(getBiEventData(additionalParams, BiEventIDs.CHANGE_PP_TYPE));

  const logPlansPageLoadedBiEvent = (
    additionalParams: BiEventParams<AppLoadBiEvent>,
  ) =>
    logBiEvent(
      getBiEventData(
        { ...additionalParams, viewMode: 'siteLevel' },
        BiEventIDs.PACKAGE_PICKER_LOADED,
      ),
    );

  const logSelectCycleBiEvent = (
    additionalParams: BiEventParams<SelectCycleBiEvent>,
  ) =>
    logBiEvent(getBiEventData(additionalParams, BiEventIDs.A_CYCLE_SELECTED));

  const logPaymentPageReadyBiEvent = (
    additionalParams: BiEventParams<CheckoutPageLoadedBiEvent>,
  ) =>
    logBiEvent(
      getBiEventData(additionalParams, BiEventIDs.A_PAYMENT_PAGE_READY),
    );

  const logStepBackBiEvent = (
    additionalParams: BiEventParams<ChangeStepBiEvent>,
  ) =>
    logBiEvent(
      getBiEventData(
        { ...additionalParams, abTesting: undefined },
        BiEventIDs.PREVIOUS_STEP_SELECT,
      ),
    );

  const logSubmitPurchaseClickedBiEvent = (
    additionalParams: BiEventParams<CheckoutPageBiEvent>,
  ) =>
    logBiEvent(
      getBiEventData(additionalParams, BiEventIDs.ORDER_PAGE_SUBMIT_PRESSED),
    );

  const logPurchaseSuccessBiEvent = (
    additionalParams: BiEventParams<CheckoutPageBiEvent>,
  ) =>
    logBiEvent(
      getBiEventData(
        additionalParams,
        BiEventIDs.R_PREMIUM_ORDER_PAGE_SUMBIT_SUCCESS,
      ),
    );

  const logPurchasePendingBiEvent = (
    additionalParams: BiEventParams<CheckoutPageBiEvent>,
  ) =>
    logBiEvent(
      getBiEventData(
        additionalParams,
        BiEventIDs.R_PREMIUM_ORDER_PAGE_SUBMIT_PENDING,
      ),
    );

  const logOrderPageBusinessErrorBiEvent = (
    additionalParams: BiEventParams<CheckoutPageBiEvent>,
  ) =>
    logBiEvent(
      getBiEventData(additionalParams, BiEventIDs.ORDER_PAGE_BUSINESS_ERROR),
    );

  const logOrderPageFatalErrorBiEvent = (
    additionalParams: BiEventParams<CheckoutPageBiEvent>,
  ) =>
    logBiEvent(
      getBiEventData(additionalParams, BiEventIDs.ORDER_PAGE_FATAL_ERROR),
    );

  const logOrderPageValidationErrorBiEvent = (
    additionalParams: BiEventParams<CheckoutPageBiEvent>,
  ) =>
    logBiEvent(
      getBiEventData(additionalParams, BiEventIDs.ORDER_PAGE_VALIDATION_ERROR),
    );

  const logDoublePurchasePopupCTA = () =>
    logBiEvent({
      evid: BiEventIDs.A_DOUBLE_PURCHASE_POPUP_VIEW_BUTTON_CLICKED,
    });

  const logTabsTooltipClosed = () =>
    logBiEvent({ evid: BiEventIDs.TABS_TOOLTIP_CLOSED });

  const logDoublePurchasePopupViewed = (
    additionalParams: BiEventParams<DoublePurchaseBiEvent>,
  ) =>
    logBiEvent(
      getBiEventData(additionalParams, BiEventIDs.A_VIEW_DOUBLE_PURCHASE_POPUP),
    );

  const logRequestCallPopupLoad = (
    additionalParams: BiEventParams<RequestCallPopupLoadBiEvent>,
  ) =>
    logBiEvent({
      evid: BiEventIDs.REQUEST_CALL_POPUP_LOAD,
      ...additionalParams,
    });

  const logRequestCallPopupCTA = (
    additionalParams: BiEventParams<RequestCallCTABiEvent>,
  ) =>
    logBiEvent({
      evid: BiEventIDs.REQUEST_CALL_POPUP_CTA,
      ...additionalParams,
    });

  const logViewProductFeatureTooltip = (
    additionalParams: BiEventParams<ViewProductFeatureTooltipBiEvent>,
  ) =>
    logBiEvent({
      evid: BiEventIDs.VIEW_PRODUCT_FEATURE_TOOLTIP,
      ...additionalParams,
    });

  const logClickLinkInProductFeatureTooltip = (
    additionalParams: BiEventParams<ClickProductFeatureTooltipLinkBiEvent>,
  ) =>
    logBiEvent({
      evid: BiEventIDs.CLICK_PRODUCT_FEATURE_TOOLTIP_LINK,
      ...additionalParams,
    });

  const logCurrentPlanModalOpened = (
    additionalParams: BiEventParams<ViewCurrentPlanBiEvent>,
  ) =>
    logBiEvent(
      getBiEventData(
        additionalParams,
        BiEventIDs.VIEW_CURRENT_PLAN_MODAL_OPENED,
      ),
    );

  const logChangePlanPopupOpened = (
    additionalParams: BiEventParams<ChangePlanBiEvent>,
  ) =>
    logBiEvent(
      getBiEventData(additionalParams, BiEventIDs.CHANGE_PLAN_POPUP_OPENED),
    );

  const logChangePlanPopupClickChange = (
    additionalParams: BiEventParams<ChangePlanBiEvent>,
  ) =>
    logBiEvent(
      getBiEventData(
        additionalParams,
        BiEventIDs.CHANGE_PLAN_POPUP_CLICK_CHANGE,
      ),
    );

  const logChangePlanPopupClosed = (
    additionalParams: BiEventParams<ChangePlanBiEvent>,
  ) =>
    logBiEvent(
      getBiEventData(additionalParams, BiEventIDs.CHANGE_PLAN_POPUP_CLOSED),
    );

  const logDiscountTooltipIsShown = (
    additionalParams: BiEventParams<ViewCycleDiscountTooltip>,
  ) =>
    logBiEvent(
      getBiEventData(additionalParams, BiEventIDs.VIEW_CYCLE_DISCOUNT_TOOLTIP),
    );

  const logClickOnFAQItem = (additionalParams: BiEventParams<ClickOnFAQItem>) =>
    logBiEvent(getBiEventData(additionalParams, BiEventIDs.CLICK_ON_FAQ_ITEM));
  const logClickOnShowFeatures = (
    additionalParams: BiEventParams<ClickOnShowFeatures>,
  ) =>
    logBiEvent(
      getBiEventData(additionalParams, BiEventIDs.CLICK_ON_SHOW_FEATURES),
    );
  const logClickOnShowAllFeatures = (
    additionalParams: BiEventParams<ClickOnShowAllFeatures>,
  ) =>
    logBiEvent(
      getBiEventData(additionalParams, BiEventIDs.CLICK_ON_SHOW_ALL_FEATURES),
    );

  const logCheckoutPageViewTooltipBiEvent = (
    additionalParams: BiEventParams<CheckoutPageViewTooltip>,
  ) =>
    logBiEvent(
      getBiEventData(additionalParams, BiEventIDs.CHECKOUT_PAGE_VIEW_TOOLTIP),
    );

  const logSelectPlanRadioButtonsMobileGrid = (
    additionalParams: BiEventParams<SelectPlanRadioButtonsMobileGrid>,
  ) =>
    logBiEvent(
      getBiEventData(
        additionalParams,
        BiEventIDs.SELECT_PLAN_RADIO_BUTTONS_MOBILE_GRID,
      ),
    );

  const logStickyHeaderMobileGrid = (
    additionalParams: BiEventParams<StickyHeaderMobileGrid>,
  ) =>
    logBiEvent(
      getBiEventData(additionalParams, BiEventIDs.STICKY_HEADER_MOBILE_GRID),
    );

  // DAYFUL

  const logDayfulPlansPageLoadedBiEvent = (
    additionalParams: BiEventParams<AppLoadBiEvent>,
  ) =>
    logBiEvent(
      getBiEventData(
        { ...additionalParams },
        DayfulBiEventIds.PACKAGE_PICKER_LOADED,
      ),
    );

  const logDayfulCyclesPageViewBiEvent = (
    additionalParams: BiEventParams<SelectPlanBiEvent>,
  ) =>
    logBiEvent(
      getBiEventData({ ...additionalParams }, DayfulBiEventIds.VIEW_CYCLES),
    );

  const logComparePlanFeaturesCTA = (
    additionalParams: BiEventParams<BiEvent>,
  ) =>
    logBiEvent(
      getBiEventData(
        { ...additionalParams },
        BiEventIDs.COMPARE_PLAN_FEATURES_CTA,
      ),
    );

  const logSubmitCheckoutReassignDomainFlow = (
    additionalParams: BiEventParams<SubmitCheckoutReassignDomainFlow>,
  ) =>
    logBiEvent(
      getBiEventData(
        { ...additionalParams },
        BiEventIDs.SUBMIT_CHECKOUT_PLAN_REASSIGN_DOMAIN_FLOW,
      ),
    );

  const logAnonymousCtaClick = (
    additionalParams: BiEventParams<AnonymousCtaBiEvent>,
  ) =>
    logBiEvent(
      getBiEventData({ ...additionalParams }, BiEventIDs.ANONYMOUS_CTA_CLICK),
    );

  const logContinueToCheckout = (
    additionalParams: packagePickerCycleSelection2ndStepClickContinueToCheckoutSrc17Evid3925Params,
  ) => {
    loggerV2.report(
      packagePickerCycleSelection2ndStepClickContinueToCheckoutSrc17Evid3925({
        ...additionalParams,
      }),
    );
  };

  const logCheckoutLoadFailed = (
    additionalParams: packagePickerCheckoutLoadFailedSrc17Evid728Params,
  ) =>
    loggerV2.report(
      packagePickerCheckoutLoadFailedSrc17Evid728({
        ...additionalParams,
      }),
    );

  const logIsRevShareVisible = (
    additionalParams: BiEventParams<packagePickerCycleSelectionRevShareLoadedSrc17Evid3927Params>,
  ) =>
    loggerV2.report(
      packagePickerCycleSelectionRevShareLoadedSrc17Evid3927({
        ...additionalParams,
      }),
    );

  return {
    logChangeTabBiEvent,
    logPlansPageLoadedBiEvent,
    logSelectPlanBiEvent,
    logSelectCycleBiEvent,
    logPaymentPageReadyBiEvent,
    logStepBackBiEvent,
    logSubmitPurchaseClickedBiEvent,
    logPurchaseSuccessBiEvent,
    logPurchasePendingBiEvent,
    logOrderPageBusinessErrorBiEvent,
    logOrderPageFatalErrorBiEvent,
    logOrderPageValidationErrorBiEvent,
    logDoublePurchasePopupCTA,
    logDoublePurchasePopupViewed,
    logRequestCallPopupLoad,
    logRequestCallPopupCTA,
    logViewProductFeatureTooltip,
    logClickLinkInProductFeatureTooltip,
    logTabsTooltipClosed,
    logCurrentPlanModalOpened,
    logChangePlanPopupOpened,
    logChangePlanPopupClickChange,
    logChangePlanPopupClosed,
    logDiscountTooltipIsShown,
    logClickOnFAQItem,
    logClickOnShowFeatures,
    logClickOnShowAllFeatures,
    logCheckoutPageViewTooltipBiEvent,
    logSelectPlanRadioButtonsMobileGrid,
    logStickyHeaderMobileGrid,
    logDayfulPlansPageLoadedBiEvent,
    logDayfulCyclesPageViewBiEvent,
    logComparePlanFeaturesCTA,
    logSubmitCheckoutReassignDomainFlow,
    logAnonymousCtaClick,
    logContinueToCheckout,
    logCheckoutLoadFailed,
    logIsRevShareVisible,
  };
};
